import { createContext, useContext, useState, ReactNode } from 'react'

export enum AlertVariant {
  primary = 'primary',
  secondary = 'secondary',
  success = 'success',
  danger = 'danger',
  warning = 'warning',
  info = 'info',
  light = 'light',
  dark = 'dark',
}

type AppAlertMessage = {
  /* We will use interweave
   * so that consumer may pass html elements
   */
  content: string

  title?: string
  variant?: AlertVariant
  dismissable?: boolean
}

type AppAlertMessageContextType = {
  appAlertMessage: AppAlertMessage
  setAppAlertMessage: (alert: AppAlertMessage) => void
  clearAppAlertMessage: () => void
}

const AppAlertMessageContext = createContext<AppAlertMessageContextType | undefined>(undefined)

type AppAlertMessageProviderProps = {
  children: ReactNode
}

export const AppAlertMessageProvider = ({ children }: AppAlertMessageProviderProps) => {
  const [appAlertMessage, setAppAlertMessage] = useState<AppAlertMessage>({
    title: '',
    content: '',
    dismissable: false,
    variant: AlertVariant.info, // Default variant
  })

  const clearAppAlertMessage = () => {
    setAppAlertMessage({
      title: '',
      content: '',
      variant: AlertVariant.primary,
      dismissable: false,
    })
  }

  return (
    <AppAlertMessageContext.Provider value={{ appAlertMessage, setAppAlertMessage, clearAppAlertMessage }}>
      {children}
    </AppAlertMessageContext.Provider>
  )
}

// Custom Hook for Using the Context
export const useAppAlertMessage = (): AppAlertMessageContextType => {
  const context = useContext(AppAlertMessageContext)

  if (!context) {
    throw new Error('useAppAlertMessage must be used within an AppAlertMessageProvider')
  }
  return context
}
